import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Monitor, 
  Download, 
  CheckCircle, 
  Clock,
  Apple,
  Play,
  ArrowRight,
  AlertTriangle,
  Shield,
  Smartphone,
  Settings,
  Info,
  Calendar
} from 'lucide-react';
import { trackGoogleAdsConversion, CONVERSION_LABELS, trackOnboardingBooking, trackAppDownload } from '../utils';

export default function DownloadsPage() {
  const [showAndroidInstructions, setShowAndroidInstructions] = useState(false);

  const handleDownload = (platform: string, url?: string, conversionLabel?: string) => {
    // Track Google Ads conversion if label provided
    if (conversionLabel) {
      trackGoogleAdsConversion(conversionLabel);
    }
    
    // Track GA4 custom event
    trackAppDownload(platform);
    
    if (url) {
      window.open(url, '_blank');
    } else {
      // For platforms not yet available, could show a waitlist modal or message
      alert(`${platform} version coming soon! Join our waitlist to be notified.`);
    }
  };
  
  const handleOnboardingClick = () => {
    // Track both Google Ads conversion and GA4 event
    trackGoogleAdsConversion(CONVERSION_LABELS.ONBOARDING_BOOKING);
    trackOnboardingBooking();
    window.open('https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0HbZ_WQ9tR-9W0VZ_ZClyx6SL3Nee5aVcrJThIogOYlRc_ETqvaBDD5Z0UUleq8-bH_D2WZlv4', '_blank');
  };

  const androidDownloadUrl = 'https://storage.googleapis.com/wesonder-app-downloads/WeSonder-Android-v1.1.apk';

  return (
    <div className="bg-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="section-headline text-slate-900 mb-6">
            Get <span className="text-blue-500">WeSonder</span> For You
          </h1>
          <p className="body-text text-slate-600 max-w-3xl mx-auto">
            Download WeSonder on your preferred platform to start your journey of self-discovery. Get AI-powered insights into your personality and relationships through your text messages.
          </p>
          
          {/* Onboarding Session Button */}
          <div className="flex justify-center mt-8">
            <Button 
              onClick={handleOnboardingClick}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Book Your Free Onboarding Session
            </Button>
          </div>
        </div>

        {/* Main Platform Cards */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* iOS App */}
          <Card className="border-2 border-slate-200 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mr-4">
                  <Apple className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="feature-title text-slate-900">iOS App</h3>
                  <p className="text-green-600 font-medium">Available Now</p>
                </div>
              </div>
              
              <p className="body-text text-slate-600 mb-6">
                Download WeSonder on your iPhone and get AI-powered communication coaching and relationship insights on the go.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-slate-700">AI-powered communication analysis</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-slate-700">Personalized relationship insights</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-slate-700">iMessage conversation analysis</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-slate-700">Tone and timing analysis</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-slate-700">App Store approved</span>
                </div>
              </div>https://www.wesonder.io/eq-up

              <Button 
                onClick={() => handleDownload('ios', 'https://apps.apple.com/us/app/wesonder/id6749514743', CONVERSION_LABELS.IOS_DOWNLOAD)}
                className="w-full bg-slate-800 hover:bg-slate-900 text-white py-3 text-base"
              >
                <Download className="w-5 h-5 mr-2" />
                Download on App Store
              </Button>
            </CardContent>
          </Card>

          {/* Android App */}
          <Card className="border-2 border-green-200 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                  <Play className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="feature-title text-slate-900">Android App</h3>
                  <p className="text-green-600 font-medium">Available Now - Direct Download</p>
                </div>
              </div>
              
              <p className="body-text text-slate-600 mb-6">
                WeSonder for Android includes deep personality analysis functionality for comprehensive relationship insights, available as a direct download.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-slate-700">Deep personality analysis</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-slate-700">Full SMS export functionality</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-slate-700">Communication coaching</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-slate-700">Complete feature set</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-slate-700">Version 1.1 (9.6MB)</span>
                </div>
              </div>

              <div className="space-y-3">
                <Button 
                  onClick={() => handleDownload('android', androidDownloadUrl, CONVERSION_LABELS.ANDROID_DOWNLOAD)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-base"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Android APK
                </Button>
                
                <Button 
                  onClick={() => setShowAndroidInstructions(!showAndroidInstructions)}
                  variant="outline"
                  className="w-full border-green-200 text-green-700 hover:bg-green-50 py-2 text-sm"
                >
                  <Info className="w-4 h-4 mr-2" />
                  {showAndroidInstructions ? 'Hide' : 'Show'} Installation Guide
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Android Installation Instructions */}
        {showAndroidInstructions && (
          <div className="mb-16">
            <Card className="border-2 border-green-200 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <Smartphone className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="feature-title text-slate-900">Android Installation Guide</h3>
                    <p className="text-green-600 font-medium">Step-by-step instructions</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* Installation Steps */}
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 flex items-center">
                      <Download className="w-5 h-5 mr-2 text-green-600" />
                      Installation Steps
                    </h4>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                          1
                        </div>
                        <div>
                          <p className="text-slate-700 font-medium">Download the APK</p>
                          <p className="text-sm text-slate-600">Click the download button above to get the WeSonder APK file</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                          2
                        </div>
                        <div>
                          <p className="text-slate-700 font-medium">Enable Unknown Sources</p>
                          <p className="text-sm text-slate-600">Allow installation from sources other than Google Play Store</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                          3
                        </div>
                        <div>
                          <p className="text-slate-700 font-medium">Install the App</p>
                          <p className="text-sm text-slate-600">Open the downloaded APK file and follow the installation prompts</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                          4
                        </div>
                        <div>
                          <p className="text-slate-700 font-medium">Grant Permissions</p>
                          <p className="text-sm text-slate-600">Allow SMS access for relationship analysis functionality</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Security & Permissions */}
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4 flex items-center">
                      <Shield className="w-5 h-5 mr-2 text-blue-600" />
                      Security & Permissions
                    </h4>
                    
                    <div className="space-y-4">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h5 className="font-medium text-blue-900 mb-2 flex items-center">
                          <Settings className="w-4 h-4 mr-2" />
                          Enable Unknown Sources
                        </h5>
                        <div className="text-sm text-blue-800 space-y-2">
                          <p><strong>Settings → Security → Unknown Sources</strong></p>
                          <p>• Go to your Android device settings</p>
                          <p>• Navigate to Security & Privacy</p>
                          <p>• Enable "Install unknown apps" or "Unknown sources"</p>
                          <p>• This allows installation of apps outside the Play Store</p>
                        </div>
                      </div>

                      <div className="bg-green-50 rounded-lg p-4">
                        <h5 className="font-medium text-green-900 mb-2 flex items-center">
                          <Smartphone className="w-4 h-4 mr-2" />
                          SMS Permission Required
                        </h5>
                        <div className="text-sm text-green-800 space-y-2">
                          <p><strong>Why WeSonder needs SMS access:</strong></p>
                          <p>• Analyze your text conversations for relationship insights</p>
                          <p>• Provide AI-powered communication coaching</p>
                          <p>• Generate personality analysis from your messaging patterns</p>
                          <p>• All data stays on your device - we never upload your messages</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trust & Security Notice */}
                <div className="mt-6 bg-slate-50 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertTriangle className="w-5 h-5 text-orange-500 mr-3 mt-0.5" />
                    <div>
                      <h5 className="font-medium text-slate-900 mb-2">Why Direct Download?</h5>
                      <p className="text-sm text-slate-700">
                        WeSonder's advanced SMS analysis features cannot be published on Google Play Store due to their policies on message export functionality. 
                        Our direct download ensures you get the full feature set while maintaining complete privacy and security of your data.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Troubleshooting */}
                <div className="mt-6">
                  <h4 className="font-semibold text-slate-900 mb-4">Troubleshooting</h4>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="font-medium text-slate-900 mb-1">Installation fails?</p>
                      <p className="text-slate-600">Make sure "Unknown sources" is enabled in your security settings</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="font-medium text-slate-900 mb-1">App won't open?</p>
                      <p className="text-slate-600">Grant all requested permissions when prompted during first launch</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="font-medium text-slate-900 mb-1">Download issues?</p>
                      <p className="text-slate-600">Try using a different browser or check your internet connection</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="font-medium text-slate-900 mb-1">Need help?</p>
                      <p className="text-slate-600">Contact our support team for assistance with installation</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* iOS + Mac Combo Section */}
        <div className="mb-16">
          <h2 className="section-headline text-slate-900 text-center mb-12">
            Complete <span className="text-blue-500">WeSonder</span> Experience (iOS Users Only)
          </h2>
          
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-blue-200 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <Monitor className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="feature-title text-slate-900">Mac App</h3>
                    <p className="text-blue-600 font-medium">Available Now - Direct Download</p>
                  </div>
                </div>
                
                <p className="body-text text-slate-600 mb-6">
                  The Mac app works seamlessly with your iOS app to provide the complete WeSonder experience. Export messages in bulk and get deep personality analysis that goes beyond what's possible on mobile.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
                      <Apple className="w-5 h-5 mr-2 text-slate-600" />
                      iOS App
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        <span className="text-sm text-slate-700">On-the-go insights</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        <span className="text-sm text-slate-700">Real-time analysis</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        <span className="text-sm text-slate-700">Quick relationship check-ins</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <ArrowRight className="w-8 h-8 text-blue-500" />
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
                      <Monitor className="w-5 h-5 mr-2 text-blue-600" />
                      Mac App
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        <span className="text-sm text-slate-700">Bulk message export</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        <span className="text-sm text-slate-700">Deep personality analysis</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        <span className="text-sm text-slate-700">Comprehensive analytics</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>How it works:</strong> Use the iOS app for daily relationship insights and communication coaching. When you want deeper analysis, export your messages through the Mac app for deep personality analysis and comprehensive relationship insights.
                  </p>
                </div>

                <div className="space-y-3">
                  <Button 
                    onClick={() => handleDownload('mac', 'https://storage.googleapis.com/wesonder-website/downloads/WeSonder.dmg', CONVERSION_LABELS.MAC_DOWNLOAD)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-base"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download Mac App (Direct)
                  </Button>
                  
                  <p className="text-xs text-slate-500 text-center">
                    Direct download available. App Store version coming soon.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Feature Comparison */}
        <div className="bg-slate-50 rounded-2xl p-8">
          <h2 className="section-headline text-slate-900 text-center mb-12">
            Platform Comparison
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Feature</th>
                  <th className="text-center py-4 px-4 font-semibold text-slate-900">iOS App</th>
                  <th className="text-center py-4 px-4 font-semibold text-slate-900">Android App</th>
                  <th className="text-center py-4 px-4 font-semibold text-slate-900">Mac App</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="py-4 px-4 font-medium text-slate-700">AI Communication Analysis</td>
                  <td className="text-center py-4 px-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-4 px-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-4 px-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 px-4 font-medium text-slate-700">Relationship Insights</td>
                  <td className="text-center py-4 px-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-4 px-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-4 px-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 px-4 font-medium text-slate-700">Deep Personality Analysis</td>
                  <td className="text-center py-4 px-4"><Clock className="w-5 h-5 text-orange-500 mx-auto" /></td>
                  <td className="text-center py-4 px-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-4 px-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 px-4 font-medium text-slate-700">App Store Available</td>
                  <td className="text-center py-4 px-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-4 px-4"><Clock className="w-5 h-5 text-orange-500 mx-auto" /></td>
                  <td className="text-center py-4 px-4"><Clock className="w-5 h-5 text-orange-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-4 px-4 font-medium text-slate-700">Direct Download</td>
                  <td className="text-center py-4 px-4"><Clock className="w-5 h-5 text-orange-500 mx-auto" /></td>
                  <td className="text-center py-4 px-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-4 px-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
          

        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <h2 className="feature-title text-slate-900 mb-4">
            Ready to Start Your Journey with <span className="text-blue-500">WeSonder</span>?
          </h2>
          <p className="body-text text-slate-600 mb-8 max-w-2xl mx-auto">
            Download WeSonder now for AI-powered communication coaching and relationship insights on your preferred platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => handleDownload('ios', 'https://apps.apple.com/us/app/wesonder/id6749514743', CONVERSION_LABELS.IOS_DOWNLOAD)}
              className="bg-slate-800 hover:bg-slate-900 text-white px-8 py-3 text-base"
            >
              <Apple className="w-5 h-5 mr-2" />
              Download iOS App
            </Button>
            <Button 
              onClick={() => handleDownload('android', androidDownloadUrl, CONVERSION_LABELS.ANDROID_DOWNLOAD)}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-base"
            >
              <Play className="w-5 h-5 mr-2" />
              Download Android APK
            </Button>
            <Button 
              onClick={() => handleDownload('mac', 'https://storage.googleapis.com/wesonder-website/downloads/WeSonder.dmg', CONVERSION_LABELS.MAC_DOWNLOAD)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-base"
            >
              <Monitor className="w-5 h-5 mr-2" />
              Download Mac App
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
} 